"use client";

import React from "react";

export interface TechLayerTier {
    tierNum: string;
    tierName: string;
    description: string;
    specs: string[];
}

export interface MemorylayerArchitectureBlockProps {
    tag?: string;
    title?: string;
    description?: string;
    tiers?: TechLayerTier[];
}

export default function MemorylayerArchitectureBlock({
    tag = "Technical Blueprint",
    title = "System Architecture Layers",
    description = "The complete blueprint is divided into three tiers: client UI interface, middle FastAPI route parser, and PostgreSQL database storage.",
    tiers = [
        {
            tierNum: "Tier 01",
            tierName: "Client UI / Interface Layer",
            description: "Built using Next.js, tailwindcss, and lucide-react. Renders high-fidelity controls to select agent presets, stream conversation logs, and compile custom prompts in real-time.",
            specs: ["Next.js React Framework", "Tailwind CSS Layout", "Server-Sent Events Interface"]
        },
        {
            tierNum: "Tier 02",
            tierName: "Router / Parsing Logic Layer",
            description: "A Python FastAPI execution layer that parses client requests, generates 1536-dim vectors from prompt text using OpenAI embeddings, and handles multi-language polyglot rules.",
            specs: ["Python & FastAPI Server", "OpenAI Embedding API", "Token Allocation Budgets"]
        },
        {
            tierNum: "Tier 03",
            tierName: "Database / Memory Storage Layer",
            description: "A PostgreSQL backend running pgvector to query similarities. Contains index algorithms (HNSW/IVFFlat) to return prompt memories in under 15ms.",
            specs: ["PostgreSQL Backend DB", "pgvector Database Index", "Redis Session Caching"]
        }
    ]
}: MemorylayerArchitectureBlockProps) {
    return (
        <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#0A0A0C] w-full" data-header-theme="light">
            <div className="container mx-auto">
                <div className="max-w-2xl mb-16">
                    {tag && (
                        <span className="text-xs font-mono text-purple-400 uppercase tracking-widest block mb-4">// {tag}</span>
                    )}
                    <h2 className="text-4xl md:text-5xl font-light text-white leading-tight font-sans">
                        {title}
                    </h2>
                    {description && (
                        <p className="text-zinc-500 text-sm mt-2 font-sans font-light">
                            {description}
                        </p>
                    )}
                </div>

                {/* Architecture Columns */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
                    {tiers.map((tier, idx) => (
                        <div key={idx} className="bg-[#121214] border border-zinc-800 rounded-2xl p-8 flex flex-col justify-between hover:border-purple-500/20 transition-colors duration-300">
                            <div className="space-y-6">
                                <div className="flex justify-between items-center pb-4 border-b border-zinc-900">
                                    <span className="text-xs font-mono text-purple-400 uppercase tracking-widest font-semibold">{tier.tierNum}</span>
                                    <span className="w-1.5 h-1.5 rounded-full bg-purple-500" />
                                </div>
                                <h3 className="text-xl font-normal text-white">{tier.tierName}</h3>
                                <p className="text-zinc-400 text-xs leading-relaxed font-light font-sans">
                                    {tier.description}
                                </p>
                            </div>

                            <div className="mt-8 space-y-2 pt-6 border-t border-zinc-900/60">
                                <span className="text-[9px] font-mono text-zinc-500 uppercase tracking-widest block">Stack / Specifications</span>
                                <div className="flex flex-wrap gap-1.5">
                                    {tier.specs.map((spec, sIdx) => (
                                        <span key={sIdx} className="text-[10px] font-mono bg-zinc-900 text-zinc-400 px-2 py-1 rounded border border-zinc-800/40">
                                            {spec}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
